import { useState, useEffect } from 'react'
import { useSermonAdmin, scanTranscript, generateSermonSummary, generateDisplayTitle } from '../hooks/useSermons'
import { useAuth } from '../hooks/useAuth'
import { canScan as checkScanLimit, recordScans, FREE_SCAN_LIMIT } from '../lib/scanLimits'
import UpgradeSheet from './UpgradeSheet'
import EncouragementUpload from './EncouragementUpload'

const ACCENT = 'var(--gold)'
const s = {
  wrap:     { padding:'16px', fontFamily:"'Plus Jakarta Sans',sans-serif" },
  title:    { fontSize:18, fontWeight:800, color:'var(--ink)', marginBottom:4 },
  sub:      { fontSize:13, color:'var(--stone)', marginBottom:20 },
  label:    { fontSize:11, fontWeight:700, letterSpacing:'0.12em', textTransform:'uppercase', color:'var(--stone)', marginBottom:6, display:'block', marginTop:14 },
  input:    { width:'100%', background:'var(--white)', border:'1px solid var(--border)', borderRadius:10, padding:'11px 14px', color:'var(--ink)', fontSize:14, fontFamily:"'Plus Jakarta Sans',sans-serif", boxSizing:'border-box', outline:'none' },
  textarea: { width:'100%', background:'var(--white)', border:'1px solid var(--border)', borderRadius:10, padding:'11px 14px', color:'var(--ink)', fontSize:13, fontFamily:"'Plus Jakarta Sans',sans-serif", boxSizing:'border-box', outline:'none', resize:'vertical', lineHeight:1.6 },
  row:      { display:'flex', gap:10 },
  scanBtn:  { width:'100%', background:'rgba(232,200,122,0.12)', border:'1px solid rgba(232,200,122,0.3)', borderRadius:10, padding:'13px', fontSize:14, fontWeight:700, color:'var(--gold-ink)', cursor:'pointer', marginTop:12, fontFamily:"'Plus Jakarta Sans',sans-serif' " },
  saveBtn:  { width:'100%', background:ACCENT, color:'var(--ink)', border:'none', borderRadius:10, padding:'13px', fontSize:14, fontWeight:800, cursor:'pointer', marginTop:10, fontFamily:"'Plus Jakarta Sans',sans-serif" },
  disBtn:   { opacity:0.4, cursor:'default' },
  card:     { background:'var(--white)', border:'1px solid var(--border)', borderRadius:12, padding:'12px 14px', marginBottom:8 },
  refRef:   { fontSize:11, fontWeight:800, letterSpacing:'0.15em', textTransform:'uppercase', color:'var(--gold-ink)', marginBottom:4 },
  refNotes: { fontSize:13, color:'var(--ink)', lineHeight:1.55, marginBottom:8 },
  refTs:    { fontSize:12, color:'var(--stone)' },
  status:   { borderRadius:10, padding:'12px 14px', fontSize:13, lineHeight:1.5, marginTop:12 },
  ok:       { background:'rgba(80,200,150,0.1)', border:'1px solid rgba(80,200,150,0.25)', color:'#1F7A52' },
  err:      { background:'rgba(249,112,102,0.1)', border:'1px solid rgba(249,112,102,0.25)', color:'#C8372A' },
  info:     { background:'rgba(232,200,122,0.08)', border:'1px solid rgba(232,200,122,0.2)', color:'var(--gold-ink)' },
  rm:       { background:'none', border:'none', color:'var(--stone)', cursor:'pointer', fontSize:18, padding:'0 4px', lineHeight:1 },
  step:     { display:'flex', alignItems:'center', gap:10, marginBottom:20 },
  stepNum:  { width:28, height:28, borderRadius:'50%', background:'rgba(232,200,122,0.15)', border:'1px solid rgba(232,200,122,0.3)', display:'flex', alignItems:'center', justifyContent:'center', fontSize:12, fontWeight:800, color:'var(--gold-ink)', flexShrink:0 },
  stepTxt:  { fontSize:13, color:'var(--stone)', lineHeight:1.4 },
}

function extractYoutubeId(url) {
  const m = url.match(/(?:youtu\.be\/|youtube\.com\/(?:live\/|watch\?v=))([a-zA-Z0-9_-]{11})/)
  return m ? m[1] : ''
}

// Best-effort date-from-title parser. Returns an ISO date string (YYYY-MM-DD) or ''.
// Handles common formats sermon titles tend to use:
//   "Sunday 10th May 2026", "10 May 2026", "May 10 2026", "May 10, 2026",
//   "10/05/2026" (UK), "2026-05-10".
const MONTHS = { jan:0,feb:1,mar:2,apr:3,may:4,jun:5,jul:6,aug:7,sep:8,sept:8,oct:9,nov:10,dec:11,
  january:0,february:1,march:2,april:3,june:5,july:6,august:7,september:8,october:9,november:10,december:11 }
function parseDateFromTitle(title) {
  if (!title) return ''
  const cleaned = title.replace(/(\d+)(st|nd|rd|th)/gi, '$1') // strip ordinals
  const now     = new Date()
  const yearGuess = now.getFullYear()
  // "10 May 2026" or "10 May"
  let m = cleaned.match(/\b(\d{1,2})\s+([A-Za-z]+)(?:\s+(\d{4}))?\b/)
  if (m) {
    const month = MONTHS[m[2].toLowerCase()]
    if (month != null) {
      const d = new Date(parseInt(m[3] || yearGuess), month, parseInt(m[1]))
      if (!isNaN(d)) return d.toISOString().slice(0,10)
    }
  }
  // "May 10 2026", "May 10, 2026", or "May 10"
  m = cleaned.match(/\b([A-Za-z]+)\s+(\d{1,2})(?:,?\s+(\d{4}))?\b/)
  if (m) {
    const month = MONTHS[m[1].toLowerCase()]
    if (month != null) {
      const d = new Date(parseInt(m[3] || yearGuess), month, parseInt(m[2]))
      if (!isNaN(d)) return d.toISOString().slice(0,10)
    }
  }
  // ISO "2026-05-10"
  m = cleaned.match(/\b(\d{4})-(\d{1,2})-(\d{1,2})\b/)
  if (m) return `${m[1]}-${m[2].padStart(2,'0')}-${m[3].padStart(2,'0')}`
  // UK "10/05/2026"
  m = cleaned.match(/\b(\d{1,2})\/(\d{1,2})\/(\d{4})\b/)
  if (m) return `${m[3]}-${m[2].padStart(2,'0')}-${m[1].padStart(2,'0')}`
  return ''
}

export default function SermonUpload({ onSaved, churchId }) {
  const { canModerate, profile, churches } = useAuth()
  const { saveSermon, saving, error: saveError } = useSermonAdmin()

  // What this upload is — chosen up front rather than guessed from the
  // transcript afterwards (auto-detecting "encouragement" moments inside a
  // sermon scan was unreliable: no real speaker identification, and the
  // saved text/timestamp often just captured the preacher talking nearby).
  const [contentType, setContentType] = useState('sermon') // 'sermon' | 'encouragement'

  // Free-scan paywall — blocks scanning once the church's 5 free scans are used.
  const [showUpgrade, setShowUpgrade] = useState(false)
  const [scansUsed,   setScansUsed]   = useState(0)
  const myChurch = (churches || []).find(c => c.id === churchId) || null

  const [step,        setStep]        = useState('form') // 'form' | 'scanning' | 'review' | 'saved'
  const [scanError,   setScanError]   = useState('')
  const [refs,        setRefs]        = useState([])
  // Drives the scanning-state progress bar / elapsed counter.
  const [scanElapsedMs, setScanElapsedMs] = useState(0)
  // Rough expected duration for a transcript scan — used to drive the fake
  // progress bar. The bar asymptotes towards (but never reaches) 100% so it
  // doesn't claim the request is done when it isn't.
  const SCAN_ESTIMATE_MS = 12000

  // Form fields
  const [title,       setTitle]       = useState('')
  const [series,      setSeries]      = useState('')
  const [preacher,    setPreacher]    = useState('Elders')
  const [date,        setDate]        = useState('')
  const [youtubeUrl,  setYoutubeUrl]  = useState('')
  const [duration,    setDuration]    = useState('')
  const [transcript,  setTranscript]  = useState('')

  // YouTube oEmbed auto-fill
  const [ytFetching,  setYtFetching]  = useState(false)
  const [ytAutoFilled, setYtAutoFilled] = useState(null) // {title, preacher, date} of fields we filled

  // Transcript-from-URL fetch
  const [loadingTranscript, setLoadingTranscript] = useState(false)
  const [transcriptError,   setTranscriptError]   = useState('')
  const [transcriptSuccess, setTranscriptSuccess] = useState('')
  // Timestamped transcript segments from ScrapeCreators — kept alongside the
  // plain text in the textarea so we can pass timestamps through to Claude
  // when scanning. Cleared when the user edits the textarea manually.
  const [transcriptSegments,    setTranscriptSegments]    = useState(null)

  async function loadTranscriptFromUrl() {
    if (!youtubeUrl.trim()) { setTranscriptError('Add a YouTube URL above first.'); return }
    setLoadingTranscript(true); setTranscriptError(''); setTranscriptSuccess('')
    try {
      const SUPABASE_ANON_KEY = process.env.REACT_APP_SUPABASE_ANON_KEY
      const r = await fetch('https://icsdpckobituqivafuqo.supabase.co/functions/v1/claude-proxy', {
        method: 'POST',
        headers: {
          'Content-Type':  'application/json',
          'Authorization': `Bearer ${SUPABASE_ANON_KEY}`,
          'apikey':        SUPABASE_ANON_KEY,
        },
        body: JSON.stringify({
          action:      'fetch_transcript',
          youtube_url: youtubeUrl.trim(),
        }),
      })

      // Always read the body and log the full payload so we can diagnose
      // "no transcript" / credits / ScrapeCreators errors from the console.
      const rawText = await r.text()
      let data = null
      try { data = JSON.parse(rawText) } catch { /* leave as string */ }
      console.log('[fetch_transcript] HTTP', r.status, 'response:', data ?? rawText)

      // Surface credits + upstream message hints regardless of success.
      const credits = data && (data.credits_remaining ?? data.creditsRemaining ?? data.credits)
      if (credits != null) {
        console.log('[fetch_transcript] credits_remaining:', credits)
      }
      const upstreamMsg = data && (data.message || data.error || data.detail)

      if (!r.ok) {
        const parts = [`HTTP ${r.status}`]
        if (upstreamMsg) parts.push(String(upstreamMsg))
        if (credits != null) parts.push(`${credits} ScrapeCreators credits remaining`)
        throw new Error(parts.join(' — '))
      }

      // ScrapeCreators payload shape: { transcript_only_text, transcript: [{text,start,...}], ... }
      const plain = data?.transcript_only_text || ''
      const segs  = Array.isArray(data?.transcript) ? data.transcript : null
      if (!plain && !segs) {
        const parts = ['No transcript available for that video.']
        if (upstreamMsg) parts.push(String(upstreamMsg))
        if (credits != null) parts.push(`${credits} ScrapeCreators credits remaining`)
        throw new Error(parts.join(' — '))
      }
      setTranscript(plain || flattenSegs(segs) || '')
      setTranscriptSegments(segs)
      const chars = (plain || '').length || (segs ? flattenSegs(segs).length : 0)
      setTranscriptSuccess(`Fetched transcript — ${chars.toLocaleString()} characters`)
    } catch (err) {
      console.error('loadTranscriptFromUrl:', err)
      setTranscriptError(`Couldn't fetch transcript: ${err.message || err}. You can paste it manually instead.`)
    } finally {
      setLoadingTranscript(false)
    }
  }

  // Flatten a ScrapeCreators-style segment array into plain text. Used as a
  // fallback when transcript_only_text isn't present in the response.
  function flattenSegs(segs) {
    if (!Array.isArray(segs)) return ''
    return segs.map(s => String(s?.text ?? '').trim()).filter(Boolean).join(' ')
  }

  // While the scanning step is active, tick a 150ms timer so the progress
  // bar + elapsed counter advance smoothly. The bar value is derived from
  // scanElapsedMs at render time so we don't have to store a duplicate.
  useEffect(() => {
    if (step !== 'scanning') { setScanElapsedMs(0); return }
    const startedAt = Date.now()
    setScanElapsedMs(0)
    const id = setInterval(() => setScanElapsedMs(Date.now() - startedAt), 150)
    return () => clearInterval(id)
  }, [step])

  useEffect(() => {
    const id = extractYoutubeId(youtubeUrl)
    if (!id) { setYtAutoFilled(null); return }
    let cancelled = false
    setYtFetching(true)
    fetch(`https://www.youtube.com/oembed?url=${encodeURIComponent(`https://www.youtube.com/watch?v=${id}`)}&format=json`)
      .then(r => r.ok ? r.json() : Promise.reject(new Error(`oEmbed ${r.status}`)))
      .then(meta => {
        if (cancelled) return
        const filled = {}
        // Only fill empty / default fields so we never clobber what the user typed.
        if (!title.trim() && meta.title) {
          setTitle(meta.title)
          filled.title = meta.title
          const guessed = parseDateFromTitle(meta.title)
          if (!date && guessed) { setDate(guessed); filled.date = guessed }
        }
        if ((!preacher || preacher === 'Elders') && meta.author_name) {
          setPreacher(meta.author_name)
          filled.preacher = meta.author_name
        }
        setYtAutoFilled(Object.keys(filled).length ? filled : null)
      })
      .catch(err => { if (!cancelled) console.warn('YouTube oEmbed:', err) })
      .finally(() => { if (!cancelled) setYtFetching(false) })
    return () => { cancelled = true }
    // Intentionally only re-run when the URL changes - we don't want to keep
    // re-filling as the user edits the auto-filled fields.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [youtubeUrl])

  if (!canModerate) return (
    <div style={{...s.wrap}}>
      <div style={{...s.status, ...s.err}}>Only elders and admins can upload sermons.</div>
    </div>
  )

  const contentTypeToggle = (
    <div style={{ display:'flex', gap:8, marginBottom:20 }}>
      {[['sermon','Sermon'], ['encouragement','Encouragement']].map(([id, label]) => {
        const on = contentType === id
        return (
          <button key={id} type="button" onClick={() => setContentType(id)}
            style={{
              flex:1, border:'none', borderRadius:10, padding:'11px', cursor:'pointer',
              fontSize:13, fontWeight:700, fontFamily:"'Plus Jakarta Sans',sans-serif",
              background: on ? ACCENT : 'var(--white)',
              color: on ? 'var(--ink)' : 'var(--stone)',
            }}>
            {label}
          </button>
        )
      })}
    </div>
  )

  if (contentType === 'encouragement') {
    return (
      <>
        {/* EncouragementUpload has its own 16px wrap padding — only pad the
            toggle horizontally here so the two don't stack vertically. */}
        <div style={{ padding: '16px 16px 0' }}>{contentTypeToggle}</div>
        <EncouragementUpload onSaved={onSaved} churchId={churchId}/>
      </>
    )
  }

  async function handleScan() {
    if (!transcript.trim()) return
    // Enforce the free-scan limit before spending a scan.
    if (churchId) {
      const gate = await checkScanLimit(churchId, 1)
      if (!gate.allowed) {
        setScansUsed(FREE_SCAN_LIMIT - Math.max(0, gate.remaining || 0))
        setShowUpgrade(true)
        return
      }
    }
    setStep('scanning'); setScanError('')
    try {
      // When we have the timestamped segment array from ScrapeCreators, pass
      // it through so Claude can pin each verse ref to an exact timestamp
      // rather than estimating from the plain-text position.
      const found = await scanTranscript(transcript, { title, series, date }, transcriptSegments)
      setRefs(found)
      setStep('review')
    } catch (e) {
      setScanError('Could not scan transcript: ' + e.message)
      setStep('form')
    }
  }

  async function handleSave() {
    const youtubeId = extractYoutubeId(youtubeUrl)
    if (!churchId) {
      setScanError('Pick a church before uploading.')
      setStep('form')
      return
    }
    const aiSummary = await generateSermonSummary(transcript, { title, preacher, date, church_name: myChurch?.name || '' })
    const displayTitle = aiSummary ? await generateDisplayTitle(aiSummary, { title }) : null
    const { error } = await saveSermon(
      { title, series, preacher, date, youtubeId, youtubeUrl, duration, transcript, churchId, aiSummary, displayTitle },
      refs
    )
    if (!error) {
      // Count this successful scan against the church's free allowance.
      recordScans(churchId, 1).catch(e => console.warn('recordScans failed:', e))
      setStep('saved')
      setTimeout(() => { onSaved?.(); resetForm() }, 2000)
    }
  }

  function resetForm() {
    setTitle(''); setSeries(''); setPreacher('Elders'); setDate('')
    setYoutubeUrl(''); setDuration(''); setTranscript('')
    setTranscriptSegments(null); setTranscriptSuccess(''); setTranscriptError('')
    setRefs([]); setStep('form')
  }

  function removeRef(i) {
    setRefs(r => r.filter((_,idx) => idx !== i))
  }

  const youtubeId = extractYoutubeId(youtubeUrl)
  const canScan   = transcript.trim().length > 100 && title && date

  // ── SCANNING ──
  if (step === 'scanning') {
    // Fake-progress: ease towards 90% over SCAN_ESTIMATE_MS, then crawl
    // asymptotically the rest of the way. Never reaches 100% — the step
    // changes to 'review' the instant the real call resolves.
    const ratio = scanElapsedMs / SCAN_ESTIMATE_MS
    const easedPct = ratio < 1
      ? ratio * 90
      : 90 + (1 - Math.exp(-(ratio - 1) * 0.5)) * 9 // crawls 90 → 99
    const pct = Math.min(99, easedPct)
    const elapsedSec = Math.round(scanElapsedMs / 1000)
    return (
      <div style={{...s.wrap, textAlign:'center', paddingTop:60}}>
        <style>{`
          @keyframes sermon-spin { to { transform: rotate(360deg); } }
          @keyframes sermon-glyph-pulse { 0%,100% { transform: scale(1); opacity: 1; } 50% { transform: scale(1.18); opacity: 0.55; } }
          @keyframes sermon-bar-shimmer { 0% { transform: translateX(-100%); } 100% { transform: translateX(100%); } }
        `}</style>
        <div style={{
          display:'inline-flex', alignItems:'center', justifyContent:'center',
          width:64, height:64, marginBottom:20, position:'relative',
        }}>
          <span style={{
            position:'absolute', inset:0,
            border:`3px solid rgba(232,200,122,0.18)`,
            borderTopColor: ACCENT,
            borderRadius:'50%',
            animation:'sermon-spin 1.1s linear infinite',
          }}/>
          <span style={{
            fontSize:26, color:'var(--gold-ink)',
            animation:'sermon-glyph-pulse 1.6s ease-in-out infinite',
          }}>✦</span>
        </div>
        <div style={{...s.title}}>Scanning transcript…</div>
        <div style={{...s.sub, marginBottom:18}}>
          Claude is reading the sermon and finding every scripture reference. This usually takes about 10 seconds.
        </div>
        <div style={{
          maxWidth:360, margin:'0 auto',
          background:'var(--border)',
          borderRadius:999, overflow:'hidden',
          height:8, position:'relative',
        }}>
          <div style={{
            width:`${pct}%`, height:'100%',
            background:`linear-gradient(90deg, ${ACCENT}, #C9A85A)`,
            borderRadius:999, transition:'width 200ms linear',
            position:'relative', overflow:'hidden',
          }}>
            <div style={{
              position:'absolute', inset:0,
              background:'linear-gradient(90deg, transparent 0%, rgba(255,255,255,0.35) 50%, transparent 100%)',
              animation:'sermon-bar-shimmer 1.6s ease-in-out infinite',
            }}/>
          </div>
        </div>
        <div style={{marginTop:10, fontSize:12, color:'var(--stone)', fontFamily:"'Plus Jakarta Sans',sans-serif"}}>
          {elapsedSec}s elapsed
        </div>
      </div>
    )
  }

  // ── SAVED ──
  if (step === 'saved') return (
    <div style={{...s.wrap, textAlign:'center', paddingTop:60}}>
      <div style={{fontSize:40, marginBottom:16}}>✓</div>
      <div style={{...s.title}}>Sermon saved!</div>
      <div style={{...s.sub}}>Scripture references are now linked across the Bible reader.</div>
    </div>
  )

  // ── REVIEW REFS ──
  if (step === 'review') return (
    <div style={s.wrap}>
      <button type="button"
        onClick={() => setStep('form')}
        disabled={saving}
        style={{
          background:'transparent', border:'none', color:'var(--stone)',
          fontSize:13, fontWeight:600, cursor: saving ? 'default' : 'pointer',
          padding:'0 0 12px 0', fontFamily:"'Plus Jakarta Sans',sans-serif",
        }}>← Back to sermon details</button>
      <div style={s.title}>{refs.length} scripture references found</div>
      <div style={s.sub}>Review and edit before saving. You can remove any incorrect ones.</div>

      {refs.map((ref, i) => (
        <div key={i} style={s.card}>
          <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start' }}>
            <div style={s.refRef}>{ref.verseRef}</div>
            <button style={s.rm} onClick={() => removeRef(i)}>✕</button>
          </div>
          <div style={s.refNotes}>{ref.notes}</div>
        </div>
      ))}

      {refs.length === 0 && (
        <div style={{...s.status, ...s.info}}>No scripture references were found. Check the transcript is complete and try again.</div>
      )}

      <style>{`@keyframes sermon-spin { to { transform: rotate(360deg); } }`}</style>
      <button style={{...s.saveBtn, ...(saving ? s.disBtn : {}), display:'inline-flex', alignItems:'center', justifyContent:'center', gap:8}} disabled={saving} onClick={handleSave}>
        {saving && (
          <span style={{display:'inline-block',width:14,height:14,border:`2px solid var(--ink)`,borderTopColor:'transparent',borderRadius:'50%',animation:'sermon-spin 0.8s linear infinite'}}/>
        )}
        {saving ? 'Saving…' : `Save sermon + ${refs.length} references`}
      </button>
      <button style={{...s.scanBtn, marginTop:8}} disabled={saving} onClick={() => setStep('form')}>← Back to edit</button>
      {saveError && <div style={{...s.status, ...s.err}}>{saveError}</div>}
    </div>
  )

  // ── MAIN FORM ──
  return (
    <div style={s.wrap}>
      {contentTypeToggle}
      {showUpgrade && (
        <UpgradeSheet
          church={{ id: churchId, name: myChurch?.name }}
          profile={profile}
          scansUsed={scansUsed}
          onClose={() => setShowUpgrade(false)}
        />
      )}
      <div style={s.title}>Upload a sermon</div>
      <div style={s.sub}>Fill in the details, paste the transcript, and Claude will automatically find all scripture references.</div>

      {/* How it works */}
      <div style={{ background:'rgba(232,200,122,0.05)', border:'1px solid rgba(232,200,122,0.12)', borderRadius:12, padding:'14px 16px', marginBottom:20 }}>
        <div style={{ fontSize:11, fontWeight:700, color:'var(--gold-ink)', letterSpacing:'0.1em', textTransform:'uppercase', marginBottom:12 }}>How it works</div>
        {[
          ['1', 'Paste the YouTube URL - title, preacher, and date auto-fill where possible'],
          ['2', 'Paste the YouTube transcript'],
          ['3', 'Click "Scan transcript" - Claude finds every Bible reference'],
          ['4', 'Review, adjust timestamps if needed, then save'],
        ].map(([n, txt]) => (
          <div key={n} style={s.step}>
            <div style={s.stepNum}>{n}</div>
            <div style={s.stepTxt}>{txt}</div>
          </div>
        ))}
      </div>

      {/* YouTube URL - first, so the rest can auto-fill from it */}
      <label style={s.label}>
        YouTube URL
        {ytFetching && <span style={{fontWeight:400,textTransform:'none',letterSpacing:0,marginLeft:8,color:'var(--stone)'}}>fetching details…</span>}
      </label>
      <input style={s.input} type="url" placeholder="https://www.youtube.com/watch?v=…" value={youtubeUrl} onChange={e=>setYoutubeUrl(e.target.value)}/>

      {/* YouTube thumbnail preview */}
      {youtubeId && (
        <div style={{ marginTop:10, borderRadius:10, overflow:'hidden', border:'1px solid var(--border)', position:'relative' }}>
          <img src={`https://i.ytimg.com/vi/${youtubeId}/mqdefault.jpg`} alt="" style={{ width:'100%', display:'block' }} onError={e=>e.target.style.display='none'}/>
          <div style={{ position:'absolute', bottom:8, left:10, fontSize:11, fontWeight:700, color:'rgba(255,255,255,0.9)', background:'rgba(0,0,0,0.6)', padding:'3px 8px', borderRadius:6 }}>✓ Video ID: {youtubeId}</div>
        </div>
      )}

      {ytAutoFilled && (
        <div style={{...s.status, ...s.ok, marginTop:10}}>
          ✓ Auto-filled from YouTube: {Object.keys(ytAutoFilled).join(', ')}. Edit anything below if it's not right.
        </div>
      )}

      {/* Sermon details */}
      <label style={s.label}>Sermon title *</label>
      <input style={s.input} placeholder="e.g. No Condemnation" value={title} onChange={e=>setTitle(e.target.value)}/>

      <div style={s.row}>
        <div style={{flex:1}}>
          <label style={s.label}>Date *</label>
          <input style={s.input} type="date" value={date} onChange={e=>setDate(e.target.value)}/>
        </div>
        <div style={{flex:1}}>
          <label style={s.label}>Duration</label>
          <input style={s.input} placeholder="e.g. 39:16" value={duration} onChange={e=>setDuration(e.target.value)}/>
        </div>
      </div>

      <label style={s.label}>Series</label>
      <input style={s.input} placeholder="e.g. Unstoppable Kingdom" value={series} onChange={e=>setSeries(e.target.value)}/>

      <label style={s.label}>Preacher</label>
      <input style={s.input} placeholder="e.g. Elders" value={preacher} onChange={e=>setPreacher(e.target.value)}/>

      {/* Transcript */}
      <style>{`@keyframes sermon-spin { to { transform: rotate(360deg); } }`}</style>
      <div style={{display:'flex',alignItems:'flex-end',justifyContent:'space-between',gap:10,marginTop:14}}>
        <label style={{...s.label, marginTop:0, marginBottom:0, flex:1}}>
          Transcript *
          <span style={{ fontWeight:400, textTransform:'none', letterSpacing:0, marginLeft:8, color:'var(--stone)' }}>
            From YouTube: open video → ⋯ → Show transcript → copy all
          </span>
        </label>
        <button
          type="button"
          onClick={loadTranscriptFromUrl}
          disabled={loadingTranscript || !youtubeUrl.trim()}
          style={{
            background: 'rgba(232,200,122,0.12)',
            border: '1px solid rgba(232,200,122,0.35)',
            color: 'var(--gold-ink)',
            borderRadius: 8,
            padding: '6px 12px',
            fontSize: 12,
            fontWeight: 700,
            cursor: (loadingTranscript || !youtubeUrl.trim()) ? 'default' : 'pointer',
            opacity: (loadingTranscript || !youtubeUrl.trim()) ? 0.5 : 1,
            fontFamily: "'Plus Jakarta Sans',sans-serif",
            whiteSpace: 'nowrap',
            display: 'inline-flex',
            alignItems: 'center',
            gap: 6,
          }}>
          {loadingTranscript ? (
            <>
              <span style={{display:'inline-block',width:11,height:11,border:`2px solid var(--gold-ink)`,borderTopColor:'transparent',borderRadius:'50%',animation:'sermon-spin 0.8s linear infinite'}}/>
              Fetching transcript…
            </>
          ) : 'Fetch transcript'}
        </button>
      </div>
      {transcriptError && (
        <div style={{...s.status, ...s.err, marginTop:8}}>{transcriptError}</div>
      )}
      {transcriptSuccess && !transcriptError && (
        <div style={{...s.status, ...s.info, marginTop:8, background:'rgba(80,200,150,0.10)', border:'1px solid rgba(80,200,150,0.30)', color:'#1F7A52'}}>
          ✓ {transcriptSuccess}
        </div>
      )}
      <textarea
        style={{...s.textarea, minHeight:180, marginTop:8}}
        placeholder="Paste the full sermon transcript here. Or paste the YouTube URL above and tap 'Fetch transcript'. Include timestamps if available - Claude will use them to link to the right moment in the video."
        value={transcript}
        onChange={e=>{
          setTranscript(e.target.value)
          // Manual edits invalidate the timestamped array — we can't trust
          // segment boundaries to line up with edited text anymore.
          if (transcriptSegments) setTranscriptSegments(null)
          if (transcriptSuccess) setTranscriptSuccess('')
        }}
      />
      {transcript.length > 0 && (
        <div style={{ fontSize:11, color:'var(--stone)', marginTop:4 }}>
          {transcript.split(/\s+/).filter(Boolean).length.toLocaleString()} words
        </div>
      )}

      {scanError && <div style={{...s.status, ...s.err}}>{scanError}</div>}

      {!canScan && (
        <div style={{...s.status, ...s.info, marginTop:12}}>
          Add a title, date, and transcript (at least 100 words) to scan.
        </div>
      )}

      <button
        style={{...s.scanBtn, ...(canScan ? {} : s.disBtn)}}
        disabled={!canScan}
        onClick={handleScan}
      >
        ✦ Scan transcript for scripture references
      </button>
    </div>
  )
}

// ── SERMON LIST (for admin view) ─────────────────────────────

export function SermonList({ sermons, onDelete }) {
  const { canModerate } = useAuth()

  if (sermons.length === 0) return (
    <div style={{ padding:'16px', textAlign:'center', color:'var(--stone)', fontSize:13, paddingTop:40 }}>
      No sermons uploaded yet.
    </div>
  )

  return (
    <div style={{ padding:'16px' }}>
      {sermons.map(s => (
        <div key={s.id} style={{ background:'var(--white)', border:'1px solid var(--border)', borderRadius:12, padding:'14px', marginBottom:10 }}>
          <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', marginBottom:6 }}>
            <div>
              <div style={{ fontSize:11, fontWeight:700, color:'var(--gold-ink)', letterSpacing:'0.12em', textTransform:'uppercase', marginBottom:3 }}>
                {s.series && `${s.series} · `}{new Date(s.date).toLocaleDateString('en-GB',{day:'numeric',month:'short',year:'numeric'})}
              </div>
              <div style={{ fontSize:16, fontWeight:700, color:'var(--ink)' }}>{s.title}</div>
              <div style={{ fontSize:12, color:'var(--stone)', marginTop:2 }}>{s.preacher} · {s.sermon_refs?.length || 0} scripture references</div>
            </div>
            {canModerate && (
              <button onClick={() => onDelete(s.id)} style={{ background:'none', border:'none', color:'rgba(249,112,102,0.7)', cursor:'pointer', fontSize:18, padding:'0 4px', flexShrink:0 }}>🗑</button>
            )}
          </div>
          {s.youtube_id && (
            <a href={`https://www.youtube.com/watch?v=${s.youtube_id}`} target="_blank" rel="noreferrer"
              style={{ display:'inline-flex', alignItems:'center', gap:6, background:'rgba(255,0,0,0.1)', border:'1px solid rgba(255,0,0,0.2)', borderRadius:8, padding:'5px 10px', fontSize:12, fontWeight:600, color:'var(--ink)', textDecoration:'none', marginTop:6 }}>
              ▶ Watch on YouTube
            </a>
          )}
          {s.sermon_refs && s.sermon_refs.length > 0 && (
            <div style={{ display:'flex', flexWrap:'wrap', gap:6, marginTop:10 }}>
              {s.sermon_refs.map(r => (
                <span key={r.id} style={{ background:'rgba(232,200,122,0.1)', border:'1px solid rgba(232,200,122,0.2)', borderRadius:6, padding:'2px 8px', fontSize:11, fontWeight:700, color:'var(--gold-ink)' }}>
                  {r.verse_ref}
                </span>
              ))}
            </div>
          )}
        </div>
      ))}
    </div>
  )
}
